import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import { alpha, styled } from '@mui/material/styles';
import { useLanguageSwitcher } from '@src/modules/react_hooks/useTranslation';
import { selectFocusMode } from '@src/store/slices/focus_mode';
import type { ReactElement } from 'react';
import { useSelector } from 'react-redux';

/**
 * スタイル付きボタングループコンポーネント - フォーカスモード対応
 */
const StyledButtonGroup = styled(ButtonGroup, {
  shouldForwardProp: (prop) => prop !== 'data-focus-mode',
})<{ 'data-focus-mode'?: boolean }>(({ theme, 'data-focus-mode': isFocusMode }) => {
  const accentColor = theme.palette.success.main;
  const baseBorder = isFocusMode ? 'rgba(255,255,255,0.45)' : alpha(accentColor, 0.35);
  const surface = isFocusMode ? 'rgba(255,255,255,0.08)' : alpha(accentColor, 0.08);
  const hoverSurface = isFocusMode ? 'rgba(255,255,255,0.18)' : alpha(accentColor, 0.18);
  const activeSurface = isFocusMode ? 'rgba(255,255,255,0.12)' : alpha(accentColor, 0.12);

  return {
    borderRadius: 999,
    overflow: 'hidden',
    minWidth: 'fit-content',
    boxShadow: isFocusMode ? '0 4px 12px rgba(0, 0, 0, 0.25)' : '0 8px 18px rgba(15, 23, 42, 0.12)',
    backdropFilter: isFocusMode ? 'blur(10px)' : 'none',
    backgroundColor: surface,
    border: `1px solid ${baseBorder}`,
    padding: 2,
    display: 'inline-flex',
    alignItems: 'center',
    '& .MuiButton-root': {
      border: 'none',
      fontWeight: 600,
      fontSize: '0.8rem',
      minWidth: 44,
      height: 36,
      padding: '6px 18px',
      color: isFocusMode ? 'rgba(255,255,255,0.9)' : accentColor,
      transition: 'all 0.24s ease',
      borderRadius: 999,
      position: 'relative',
      '&:hover': {
        backgroundColor: hoverSurface,
        color: isFocusMode ? 'rgba(255,255,255,0.95)' : accentColor,
        boxShadow: isFocusMode
          ? '0 6px 18px rgba(0, 0, 0, 0.28)'
          : '0 10px 22px rgba(15, 23, 42, 0.18)',
        transform: 'translateY(-1px)',
      },
      '&:active': {
        backgroundColor: activeSurface,
        transform: 'translateY(0)',
        boxShadow: isFocusMode
          ? '0 3px 10px rgba(0, 0, 0, 0.24)'
          : '0 6px 14px rgba(15, 23, 42, 0.16)',
      },
      '&:focus-visible': {
        outline: 'none',
        boxShadow: `0 0 0 3px ${alpha(accentColor, isFocusMode ? 0.35 : 0.25)}`,
      },
    },
    '& .MuiButtonGroup-grouped': {
      borderRadius: 999,
      border: 'none',
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 8,
        bottom: 8,
        right: -1,
        width: 1,
        backgroundColor: isFocusMode ? 'rgba(255,255,255,0.3)' : alpha(accentColor, 0.3),
        pointerEvents: 'none',
      },
      '&:last-of-type::after': {
        display: 'none',
      },
      '&.Mui-disabled': {
        opacity: 0.6,
      },
    },
    '& .MuiButton-contained': {
      backgroundColor: hoverSurface,
      color: isFocusMode ? '#ffffff' : accentColor,
      boxShadow: isFocusMode
        ? '0 6px 16px rgba(0, 0, 0, 0.28)'
        : `0 8px 18px ${alpha(accentColor, 0.28)}`,
      transform: 'translateY(-1px)',
      '&:hover': {
        backgroundColor: hoverSurface,
      },
      '&::after': {
        display: 'none',
      },
    },
    '& .MuiButton-outlined': {
      backgroundColor: 'transparent',
    },
  };
});

/**
 * 言語切り替えコンポーネント
 *
 * 日本語と英語の切り替えを提供するコンポーネントです。
 * 現在選択されている言語がハイライト表示されます。
 *
 * @returns {ReactElement} 言語切り替えコンポーネント
 *
 * @example
 * ```tsx
 * <LanguageSwitcher />
 * ```
 *
 * @since 1.0.0
 * @author Resume Commit Team
 */
const LanguageSwitcher: React.FC = (): ReactElement => {
  const { changeLanguage, isJapanese, isEnglish } = useLanguageSwitcher();
  const isFocusMode = useSelector(selectFocusMode) || false;

  return (
    <StyledButtonGroup variant="outlined" size="small" data-focus-mode={isFocusMode}>
      <Button
        onClick={() => changeLanguage('ja')}
        variant={isJapanese ? 'contained' : 'outlined'}
        className={isJapanese ? 'MuiButton-contained' : 'MuiButton-outlined'}
      >
        JA
      </Button>
      <Button
        onClick={() => changeLanguage('en')}
        variant={isEnglish ? 'contained' : 'outlined'}
        className={isEnglish ? 'MuiButton-contained' : 'MuiButton-outlined'}
      >
        EN
      </Button>
    </StyledButtonGroup>
  );
};

export default LanguageSwitcher;
